import Navbar from "@/components/Navbar.tsx";
import {Client, ClientRequest, User} from "@/utils/types.tsx";
import React, {useEffect, useMemo, useState} from "react";
import {ClientService} from "@/apis/client/ClientService.tsx";
import {UserService} from "@/apis/profile/UserService.tsx";
import {PencilIcon, TrashIcon} from "@heroicons/react/24/outline";
import {useLocation} from "react-router-dom";
import axios from 'axios';

const ClientsPage = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [currentUser, setCurrentUser] = useState<User>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [newClient, setNewClient] = useState<ClientRequest>({name: "", address: "", email: "", phone: ""}); // Ensure the type is MaterialRequest
    const [showForm, setShowForm] = useState(false); // State to control form visibility
    const [showEditForm, setShowEditForm] = useState(false); // State to control form visibility
    const [clientData, setClientData] = useState<Client>({
        id: 0,
        name: "",
        address: "",
        phone: "",
        email: ""
    });
    const [editClientId, setEditClientId] = useState<number>();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get("editId");

    useEffect(() => {
        if (editId) {
            setEditClientId(parseInt(editId, 10));
            setShowEditForm(true); // Open the edit form
        }
    }, [editId]);

    useEffect(() => {
        loadUserInfo();
    }, []);

    const loadUserInfo = async () => {
        try {
            const user = await UserService.getCurrentUser();
            setCurrentUser(user);
        } catch (err) {
            setError("Failed to load user information");
            setIsLoading(false);
        }
    };

    const loadClients = async () => {
        try {
            setIsLoading(true);
            const clientsData = await ClientService.getClients();
            setClients(clientsData);
        } catch (err) {
            setError("Failed to load clients");
        } finally {
            setIsLoading(false);
        }
    };

    const loadClient = async () => {
        if (editClientId) {
            const client = await ClientService.getClientById(editClientId);
            setClientData({
                id: client.id,
                name: client.name,
                address: client.address,
                phone: client.phone,
                email: client.email
            });
        }
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setClientData(prevState => ({...prevState, [name]: value}));
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const clientToSubmit = {...clientData};
        await ClientService.updateClient(Number(editClientId), clientToSubmit);
        setShowEditForm(false); // Hide the form after submission
        alert("Client actualizat cu succes");
        loadClients();
    };

    useEffect(() => {
        if (editClientId) {
            loadClient();
        }
    }, [editClientId]);

    useEffect(() => {
        if (currentUser && currentUser.username && currentUser.password && currentUser.id) {
            loadClients();
        }
    }, [currentUser]);

    const filteredClients = useMemo(() => {
        return clients.filter(
            (client) =>
                client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [clients, searchTerm]);

    const handleDelete = async (clientId: number) => {
        if (confirm("Sigur doriți să ștergeți acest client?")) {
            try {
                await ClientService.deleteClient(clientId);
                setClients(clients.filter((client) => client.id !== clientId));
            } catch (error) {
                if (axios.isAxiosError(error) && error.response) {
                    alert(error.response.data);
                } else {
                    alert("An unexpected error occurred.");
                }
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setNewClient((prev) => ({...prev, [name]: value}));
    };

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            await ClientService.addClient(newClient); // Call the service to add the material
            setNewClient({name: "", address: "", email: "", phone: ""}); // Reset the form
            setShowForm(false); // Hide the form after submission
            loadClients(); // Reload materials to include the new one
        } catch (error) {
            console.error("Failed to add client:", error);
        }
    };

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#0C1911]">
                <div className="bg-[#FFFFFF] p-6 rounded-lg shadow-md">
                    <h2 className="text-2xl font-bold mb-4 text-[#C51321]">Error</h2>
                    <p className="text-[#3C4741]">{error}</p>
                </div>
            </div>
        );
    }

    return (
      <div className="min-h-screen bg-[#FFFFFF]">
        <Navbar />
        <div className="container mx-auto px-4 py-10">
          <div className="rounded-lg bg-[#FFFFFF] p-6 shadow-md">
            <h1 className="mb-6 text-2xl font-bold text-[#C51321]">Clienți</h1>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <input
                type="text"
                placeholder="Caută clienți..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-md rounded-md border border-[#858C88] px-4 py-2 text-[#3C4741] focus:outline-none focus:ring-2 focus:ring-[#C51321]"
              />
              <button
                className="hover:bg-blue-700 mt-4 rounded bg-black px-4 py-2 text-white"
                onClick={() => {
                  setShowForm((prev) => !prev);
                  setShowEditForm(false);
                }}
              >
                Adaugă Client
              </button>
            </div>
              <div>{showForm && ( // Render form conditionally
                  <form onSubmit={handleFormSubmit} className="mt-4 py-3">
                      <div>
                          <label className="mb-2 block">Nume:</label>
                          <input
                              type="text"
                              name="name"
                              value={newClient.name}
                              onChange={handleInputChange}
                              className="border-gray-300 w-full border p-2"
                              required
                          />
                      </div>
                      <div className="mt-2">
                          <label className="mb-2 block">Adresă:</label>
                          <input
                              type="text"
                              name="address"
                              value={newClient.address}
                              onChange={handleInputChange}
                              className="border-gray-300 w-full border p-2"
                              required
                          />
                      </div>
                      <div className="mt-2">
                          <label className="mb-2 block">Email:</label>
                          <input
                              type="text"
                              name="email"
                              value={newClient.email}
                              onChange={handleInputChange}
                              className="border-gray-300 w-full border p-2"
                          />
                      </div>
                      <div className="mt-2">
                          <label className="mb-2 block">Telefon:</label>
                          <input
                              type="text"
                              name="phone"
                              value={newClient.phone}
                              onChange={handleInputChange}
                              className="border-gray-300 w-full border p-2"
                              required
                          />
                      </div>
                      <button
                          type="submit"
                          className="hover:bg-blue-700 mt-4 rounded bg-grey px-4 py-2 text-white"
                      >
                          Adaugă
                      </button>
                  </form>
              )}</div>
            {isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#C51321]"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-[#FFFFFF]">
                  <thead className="bg-[#3C4741] text-[#FFFFFF]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Nume
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Adresă
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Telefon
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Acțiuni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#858C88]">
                    {filteredClients.map((client) => (
                      <tr
                        key={client.id}
                        className="hover:bg-[#858C88] hover:bg-opacity-10"
                      >
                        <td className="whitespace-nowrap px-6 py-4 text-[#3C4741]">
                          {client.name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[#3C4741]">
                          {client.address}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[#3C4741]">
                          {client.phone}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[#3C4741]">
                          {client.email}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-[#3C4741]">
                          <button
                            className="text-blue-500 hover:text-black"
                            onClick={() => {
                              setEditClientId(client.id);
                              console.log("clientId:" + client.id);
                              setShowEditForm((prev) => !prev);
                              setShowForm(false);
                            }}
                            title="Editează clientul"
                          >
                            <PencilIcon className="inline h-5 w-5" />
                          </button>
                          <button
                            className="text-red hover:text-red"
                            onClick={() => handleDelete(client.id)}
                            title="Șterge clientul"
                          >
                            <TrashIcon className="inline h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {showEditForm && ( // Render form conditionally
                  <form onSubmit={handleEditSubmit} className="mt-4">
                    <div>
                      <label className="mb-2 block">Nume:</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={clientData.name}
                        onChange={handleEditInputChange}
                        className="border-gray-300 w-full border p-2"
                        required
                      />
                    </div>
                    <div className="mt-2">
                      <label className="mb-2 block">Adresă:</label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={clientData.address}
                        onChange={handleEditInputChange}
                        className="border-gray-300 w-full border p-2"
                        required
                      />
                    </div>
                    <div className="mt-2">
                      <label className="mb-2 block">Email:</label>
                      <input
                        type="text"
                        id="email"
                        name="email"
                        value={clientData.email}
                        onChange={handleEditInputChange}
                        className="border-gray-300 w-full border p-2"
                      />
                    </div>
                    <div className="mt-2">
                      <label className="mb-2 block">Telefon:</label>
                      <input
                        type="text"
                        id="phone"
                        name="phone"
                        value={clientData.phone}
                        onChange={handleEditInputChange}
                        className="border-gray-300 w-full border p-2"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="hover:bg-blue-700 mt-4 rounded bg-grey px-4 py-2 text-white"
                    >
                      Salvează Modificările
                    </button>
                  </form>
                )}
              </div>
            )}


          </div>
        </div>
      </div>
    );
};

export default ClientsPage;